import { useRef, useCallback } from 'react';

/**
 * Hook for managing undoable actions with a time window
 * Implements delayed execution pattern:
 * 1. Optimistic UI update
 * 2. Schedule actual mutation after delay
 * 3. Allow cancellation within time window
 */
export function useUndoableAction<T>(
  undoWindowMs: number = 8000
) {
  const pendingActionRef = useRef<NodeJS.Timeout | null>(null);
  const rollbackCallbackRef = useRef<(() => void) | null>(null);

  /**
   * Schedule an action to execute after the undo window
   * Returns a function to cancel/undo the action
   */
  const scheduleAction = useCallback(
    (
      action: () => Promise<void>,
      rollback: () => void
    ): (() => void) => {
      // Clear any existing pending action
      if (pendingActionRef.current) {
        clearTimeout(pendingActionRef.current);
      }

      // Store rollback callback
      rollbackCallbackRef.current = rollback;

      // Schedule the action to execute after undo window
      pendingActionRef.current = setTimeout(async () => {
        try {
          await action();
          rollbackCallbackRef.current = null;
        } catch (error) {
          // If action fails, automatically rollback
          if (rollbackCallbackRef.current) {
            rollbackCallbackRef.current();
          }
          throw error;
        }
      }, undoWindowMs);

      // Return undo function
      return () => {
        if (pendingActionRef.current) {
          clearTimeout(pendingActionRef.current);
          pendingActionRef.current = null;
        }
        if (rollbackCallbackRef.current) {
          rollbackCallbackRef.current();
          rollbackCallbackRef.current = null;
        }
      };
    },
    [undoWindowMs]
  );

  /**
   * Cleanup on unmount
   */
  const cleanup = useCallback(() => {
    if (pendingActionRef.current) {
      clearTimeout(pendingActionRef.current);
      pendingActionRef.current = null;
    }
    rollbackCallbackRef.current = null;
  }, []);

  return { scheduleAction, cleanup };
}
